/**
 * `<RenameDialog>` — name-input dialog for the "Rename existing cache" flow.
 *
 * Architecture §9.8 / UX §5.2 State 7 / Story FFCS-FFCS-E003-S014.
 *
 * Composition:
 *   - `lib/ui/dialog` shim (Radix-backed, canonical implementation).
 *   - Siesa UI Kit `Input` (label, error state built-in).
 *   - Siesa UI Kit `Button` for Cancel (outline) and Rename (filled).
 *   - lucide-react `Loader2` spinner while a mutation is in-flight.
 *   - lucide-react `X` for the close button.
 *   - `useFormCacheSelectorSchema` — Zod schema with i18n-bound messages.
 *
 * Behavior:
 *   - When opened, the input is pre-filled with `currentName` and focused.
 *     Re-initialisation happens on every false → true transition of `open`
 *     (R-001) — guarantees a stale value never bleeds in.
 *   - Character counter rendered as `{n} / 50` right-aligned below the input.
 *   - On submit:
 *       1. Run Zod validation locally — show inline error and short-circuit
 *          if invalid.
 *       2. Call `onSubmit(name)`. The parent hook handles the API call,
 *          409 surfacing, success banner, and dialog dismissal.
 *   - When the user types, both the local validation error and any server
 *     error (e.g. 409) are cleared so the user can retry without confusion.
 *   - `serverError` is a translation key supplied by the parent (e.g.
 *     `errors.nameAlreadyExists`). The dialog renders it through `t(...)`.
 */

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';
import { Button } from '@components/Button/Button';
import { Input } from '@components/Input/Input';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../lib/ui/dialog';
import { useFormCacheSelectorSchema } from '../FormCacheSelector.schema';

export interface RenameDialogProps {
  /** Whether the dialog is currently visible. Controlled by the parent. */
  open: boolean;
  /** Toggle handler — invoked when the dialog wants to close (e.g. cancel). */
  onOpenChange: (open: boolean) => void;
  /** Current cache name — pre-fills the input every time the dialog opens. */
  currentName: string;
  /**
   * Submit handler. Receives the trimmed user-supplied name. The parent is
   * responsible for the actual rename call, the 409 → serverError mapping,
   * and dismissing the dialog on success / 404.
   */
  onSubmit: (name: string) => Promise<void>;
  /** True while any mutation is in-flight — disables submit, shows spinner. */
  isMutating: boolean;
  /**
   * Translation key for a server-side error (typically a 409 conflict) to
   * render inline. `null` when there is no server error to show.
   */
  serverError: string | null;
  /** Clears the server error — called as the user starts editing the input. */
  onServerErrorClear: () => void;
}

export function RenameDialog({
  open,
  onOpenChange,
  currentName,
  onSubmit,
  isMutating,
  serverError,
  onServerErrorClear,
}: RenameDialogProps) {
  const { t } = useTranslation('form-cache-selector');
  const schema = useFormCacheSelectorSchema();

  const [name, setName] = useState(currentName);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Re-initialise the input from `currentName` whenever the dialog opens
  // (R-001). Clear any stale server error so a previous 409 doesn't bleed
  // into a fresh open. Defer focus + select to the next microtask so the
  // input is mounted (R-003).
  useEffect(() => {
    if (open) {
      setName(currentName);
      setLocalError(null);
      if (serverError !== null) onServerErrorClear();
      queueMicrotask(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentName]);

  const handleChange = (value: string) => {
    setName(value);
    if (localError !== null) setLocalError(null);
    if (serverError !== null) onServerErrorClear();
  };

  const runSubmit = async () => {
    const trimmed = name.trim();
    const result = schema.safeParse({ name: trimmed });
    if (!result.success) {
      const firstIssue = result.error.issues[0];
      setLocalError(firstIssue?.message ?? t('validation.name.required'));
      return;
    }
    setLocalError(null);
    await onSubmit(trimmed);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await runSubmit();
  };

  const errorMessage =
    localError !== null
      ? localError
      : serverError !== null
        ? t(serverError)
        : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rename-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle>{t('renameDialog.title')}</DialogTitle>
            <DialogClose data-testid="rename-dialog-close">
              <X className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('renameDialog.cancel')}</span>
            </DialogClose>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="mt-4">
          <Input
            ref={inputRef}
            autoFocus
            data-testid="rename-dialog-input"
            label={t('renameDialog.nameLabel')}
            value={name}
            onChange={(e) => handleChange(e.target.value)}
            maxLength={51}
            required
            error={errorMessage !== null}
          />
          <div className="mt-1 flex items-start justify-between text-xs">
            {errorMessage !== null ? (
              <span role="alert" data-testid="rename-dialog-error" className="text-red-600">
                {errorMessage}
              </span>
            ) : (
              <span />
            )}
            <span data-testid="rename-char-counter" className="text-gray-400">
              {name.length} / 50
            </span>
          </div>
          <DialogFooter>
            <Button
              htmlType="button"
              type="outline"
              size="sm"
              data-testid="rename-dialog-cancel"
              onClick={() => onOpenChange(false)}
              disabled={isMutating}
            >
              {t('renameDialog.cancel')}
            </Button>
            <Button
              htmlType="button"
              size="sm"
              data-testid="rename-dialog-submit"
              disabled={isMutating}
              onClick={() => { void runSubmit(); }}
              leftIcon={
                isMutating ? (
                  <Loader2 className="h-4 w-4 animate-spin" data-testid="rename-spinner" />
                ) : undefined
              }
            >
              {t('renameDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
